import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, StatusTabs, Controls, List, Row, Empty, TextLink, Loading, humanize } from "@/components/calm/Calm";
import { deskFilterTabs, filterByDeskTab, initialDeskTabFromSearch, isClosedProperty } from "@/lib/propertyFilters";
import { useAuth } from "@/contexts/AuthContext";
import { bookShootHref } from "@/lib/navConfig";
import { BookMediaLink } from "@/components/listing/BookMedia";


export default function TeamPropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => initialDeskTabFromSearch(location.search));
  const navigate = useNavigate();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';

  useEffect(() => {
    api.get("/properties")
      .then(r => { setProperties(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const deskTabs = deskFilterTabs(properties);
  const filtered = filterByDeskTab(properties, activeTab);
  const closedCount = deskTabs.find(x => x.id === 'Closed')?.count || 0;
  const openCount = deskTabs.find(x => x.id === 'Open')?.count ?? (properties.length - closedCount);
  const files = properties.reduce((n, x) => n + (Number(x.files_count) || 0), 0);
  const recent = properties.filter(x => x.thumbnail_url).slice(0, 4);

  const libraryStatus = (prop) => {
    const st = String(prop.status || '').toLowerCase();
    if (isClosedProperty(prop) && !['completed', 'delivered'].includes(st)) return 'Closed, still live';
    if (['completed', 'delivered'].includes(st)) return 'Delivered';
    if (['in_progress', 'editing', 'processing'].includes(st)) return 'In production';
    if (['scheduled', 'assigned', 'pending'].includes(st)) return 'Shoot booked';
    return humanize(prop.status) || 'Open';
  };

  return (
    <Page testid="team-properties-page">
      <PageHeader title="Media library"
        subtitle="Every listing on your team, with its photos, video and files."
        action={<BookMediaLink dark={dark} href={bookShootHref({ user })} />} />

      <Stats items={[
        { label: 'Open listings', value: openCount },
        { label: 'Closed', value: closedCount },
        { label: 'Files', value: files },
      ]} />

      {recent.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 8 }} data-testid="team-properties-recent">
          {recent.map(prop => (
            <button key={prop.property_id} type="button" onClick={() => navigate(`/properties/${prop.property_id}`)} aria-label={prop.address || 'Open listing'}
              style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'block' }}>
              <img src={prop.thumbnail_url} alt={prop.address || ''} style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 2, display: 'block' }} />
            </button>
          ))}
        </div>
      )}

      <Controls>
        <StatusTabs testid="team-properties-status-filter" value={activeTab} onChange={setActiveTab}
          options={deskTabs.map(t => ({ key: t.id, label: t.id }))} />
      </Controls>

      {filtered.length === 0 ? (
        <Empty action={<TextLink href={bookShootHref({ user })}>Book media</TextLink>}>
          {properties.length === 0 ? 'No listings yet. Book a shoot and the media lands here.' : 'No listings in this view.'}
        </Empty>
      ) : (
        <List testid="team-properties-list">
          {filtered.map(prop => {
            const n = Number(prop.files_count) || 0;
            return (
              <Row key={prop.property_id} testid="property-row"
                onClick={() => navigate(`/properties/${prop.property_id}`)}
                title={prop.address || 'Untitled listing'}
                meta={[prop.agent_name, `${n} ${n === 1 ? 'file' : 'files'}`].filter(Boolean)}
                next={prop.last_activity ? [`Last activity ${fmtDay(prop.last_activity)}`] : [n ? 'Files ready' : 'Media arrives after the shoot']}
                status={libraryStatus(prop)} />
            );
          })}
        </List>
      )}
    </Page>
  );
}
